"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type MutableRefObject,
} from "react";
import { usePathname } from "next/navigation";
import { useWindowSearchParams } from "@/hooks/use-window-search-params";
import posthog from "posthog-js";
import { readPosthogDistinctId } from "@/lib/analytics/posthog-identity";
import { readUtmFromSearchParams, type UtmTracking } from "./use-utm-tracking";
import { isUnknownRecord } from "@shared/validation/narrow";
import { redactUrlCredentials } from "@shared/security/redact-url";
import { readSourceTool } from "@/lib/analytics/tool-events";
import type { PublicTool } from "@shared/analytics/tools";
import { PAID_MEDIUMS } from "@convex/constants/attribution";

const FIRST_TOUCH_KEY = "__repackify_first_touch";
const FIRST_TOUCH_VERSION = 1;
const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;
const CLIENT_META_ENDPOINT = "/api/client-meta";

export interface LeadFirstTouch {
  landingPage?: string;
  referrerUrl?: string;
  fbp?: string;
  fbc?: string;
  gaClientId?: string;
  posthogDistinctId?: string;
  posthogSessionId?: string;
  ipAddress?: string;
  userAgent?: string;
  sourceTool?: PublicTool;
}

interface ClientMeta {
  ipAddress?: string;
  userAgent?: string;
}

interface FirstTouchPayload extends LeadFirstTouch {
  utm: UtmTracking;
  capturedAt: number;
  version: number;
}

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const prefix = `${name}=`;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(prefix));
  return match ? decodeURIComponent(match.slice(prefix.length)) : undefined;
}

function normalizeGaClientId(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const match = raw.match(/^GA1\.\d+\.(.+)$/);
  return match ? match[1] : raw;
}

function readPosthogSessionId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return posthog.get_session_id();
  } catch {
    return undefined;
  }
}

function isFirstTouchPayload(value: unknown): value is FirstTouchPayload {
  if (!isUnknownRecord(value)) return false;
  const record = value;
  if (record.version !== FIRST_TOUCH_VERSION) return false;
  if (typeof record.capturedAt !== "number") return false;
  if (!record.utm || typeof record.utm !== "object") return false;
  return true;
}

function readStoredFirstTouch(): FirstTouchPayload | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(FIRST_TOUCH_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isFirstTouchPayload(parsed)) return null;
    if (Date.now() - parsed.capturedAt > ONE_YEAR_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredFirstTouch(payload: FirstTouchPayload): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(payload));
  } catch {
  }
}

const normalizeUtm = readUtmFromSearchParams;

function hasAnyUtm(utm: UtmTracking): boolean {
  return Object.values(utm).some((v) => v !== undefined);
}

function attributedUtm(stored: FirstTouchPayload | null, current: UtmTracking): UtmTracking {
  const selected = hasPaidClickId(current)
    ? current
    : stored && hasAnyUtm(stored.utm) ? stored.utm : current;
  return { ...selected, attributionModel: "last_paid_click_or_first_touch_v1" };
}

function hasPaidFacebookClick(utm: UtmTracking): boolean {
  const medium = utm.utmMedium?.toLowerCase().trim();
  return Boolean(utm.fbclid && medium && PAID_MEDIUMS.has(medium));
}

function hasPaidClickId(utm: UtmTracking): boolean {
  return Boolean(
    utm.gclid ||
    utm.gbraid ||
    utm.wbraid ||
    hasPaidFacebookClick(utm) ||
    utm.msclkid ||
    utm.ttclid ||
    utm.liFatId,
  );
}

export function captureFirstTouchFromLocation(): void {
  if (typeof window === "undefined") return;
  const currentUtm = readUtmFromSearchParams(
    new URLSearchParams(window.location.search),
  );
  const stored = readStoredFirstTouch();
  if (stored && !hasPaidClickId(currentUtm)) return;

  const landing = new URL(redactUrlCredentials(window.location.href));
  writeStoredFirstTouch({
    landingPage: landing.pathname + landing.search,
    referrerUrl: document.referrer || undefined,
    utm: currentUtm,
    capturedAt: Date.now(),
    version: FIRST_TOUCH_VERSION,
  });
}

let clientMetaRequest: Promise<ClientMeta | null> | null = null;

function toClientMeta(value: unknown): ClientMeta {
  if (!isUnknownRecord(value)) return {};
  return {
    ipAddress: typeof value.ipAddress === "string" ? value.ipAddress : undefined,
    userAgent: typeof value.userAgent === "string" ? value.userAgent : undefined,
  };
}

async function fetchClientMeta(): Promise<ClientMeta> {
  const response = await fetch(CLIENT_META_ENDPOINT, { cache: "no-store" });
  if (!response.ok) throw new Error(`client-meta responded ${response.status}`);
  return toClientMeta(await response.json());
}

function requestClientMeta(): Promise<ClientMeta | null> {
  clientMetaRequest ??= fetchClientMeta().catch(() => {
    clientMetaRequest = null;
    return null;
  });
  return clientMetaRequest;
}

function useClientMeta(): MutableRefObject<ClientMeta> {
  const clientMetaRef = useRef<ClientMeta>({});
  useEffect(() => {
    let cancelled = false;
    const keep = (clientMeta: ClientMeta | null) => {
      if (!cancelled && clientMeta) clientMetaRef.current = clientMeta;
      return clientMeta;
    };
    void requestClientMeta()
      .then(keep)
      .then((clientMeta) => clientMeta ?? (cancelled ? null : requestClientMeta().then(keep)));
    return () => {
      cancelled = true;
    };
  }, []);
  return clientMetaRef;
}

function readFirstTouch(clientMeta: ClientMeta, currentUtm: UtmTracking): LeadFirstTouch {
  const stored = readStoredFirstTouch();
  const landing = typeof window !== "undefined" && hasPaidClickId(currentUtm)
    ? new URL(redactUrlCredentials(window.location.href))
    : null;
  const fallbackUserAgent =
    typeof navigator !== "undefined" ? navigator.userAgent : undefined;
  return {
    landingPage: landing ? landing.pathname + landing.search : stored?.landingPage,
    referrerUrl: landing ? document.referrer || undefined : stored?.referrerUrl,
    fbp: readCookie("_fbp"),
    fbc: readCookie("_fbc"),
    gaClientId: normalizeGaClientId(readCookie("_ga")),
    posthogDistinctId: readPosthogDistinctId(),
    posthogSessionId: readPosthogSessionId(),
    ipAddress: clientMeta.ipAddress,
    userAgent: clientMeta.userAgent ?? fallbackUserAgent,
    sourceTool: readSourceTool(),
  };
}

export function useLeadAttribution(): {
  utm: UtmTracking;
  firstTouch: () => LeadFirstTouch;
} {
  const searchParams = useWindowSearchParams();
  const pathname = usePathname();
  const currentUtm = useMemo(() => normalizeUtm(searchParams), [searchParams]);
  const clientMetaRef = useClientMeta();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = readStoredFirstTouch();
    if (stored && !hasPaidClickId(currentUtm)) return;

    const landingPage = pathname
      ? `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`
      : window.location.pathname + window.location.search;

    const payload: FirstTouchPayload = {
      landingPage,
      referrerUrl: document.referrer || undefined,
      utm: currentUtm,
      capturedAt: Date.now(),
      version: FIRST_TOUCH_VERSION,
    };
    writeStoredFirstTouch(payload);
  }, [currentUtm, pathname, searchParams]);

  const utm = useMemo(() => {
    const stored = readStoredFirstTouch();
    return attributedUtm(stored, currentUtm);
  }, [currentUtm]);

  const firstTouch = useCallback(
    (): LeadFirstTouch => readFirstTouch(clientMetaRef.current, currentUtm),
    [clientMetaRef, currentUtm],
  );

  return { utm, firstTouch };
}

export interface LeadAttributionSnapshot {
  utm: UtmTracking;
  firstTouch: LeadFirstTouch;
  sourcePath: string;
  sourceReferrer?: string;
}

export function useSubmitTimeAttribution(): () => LeadAttributionSnapshot {
  const clientMetaRef = useClientMeta();

  return useCallback((): LeadAttributionSnapshot => {
    const stored = readStoredFirstTouch();
    const currentUtm =
      typeof window === "undefined"
        ? {}
        : readUtmFromSearchParams(new URLSearchParams(window.location.search));
    return {
      utm: attributedUtm(stored, currentUtm),
      firstTouch: readFirstTouch(clientMetaRef.current, currentUtm),
      sourcePath:
        typeof window === "undefined"
          ? ""
          : window.location.pathname + window.location.search,
      sourceReferrer:
        typeof document === "undefined"
          ? undefined
          : document.referrer || undefined,
    };
  }, [clientMetaRef]);
}
